import type { ReactNode } from 'react';
import type { ContentIdea, WeekPlan } from '@content-planner/shared';

type Props = {
  week: number;
  month: string;
  plan: WeekPlan | undefined;
  loading: boolean;
  /** Optional per-idea footer slot (used on the results page for drafting). */
  ideaSlot?: (idea: ContentIdea) => ReactNode;
};

const PLATFORM_LABEL: Record<string, string> = {
  linkedin: 'LinkedIn',
  instagram: 'Instagram',
  facebook: 'Facebook',
};

const DAY_LABEL: Record<string, string> = {
  monday: 'Monday',
  wednesday: 'Wednesday',
  friday: 'Friday',
};

export function WeekDisplay({ week, month, plan, loading, ideaSlot }: Props) {
  return (
    <section className="mb-8">
      <header className="mb-4 flex items-baseline justify-between">
        <h3>
          Week {week}
          <span className="ml-2 text-text-secondary text-base font-normal">
            {plan?.dateRange || month}
          </span>
        </h3>
      </header>

      {loading && !plan && (
        <div className="gcm-card">
          <p className="text-text-secondary">Drafting your week {week} plan...</p>
        </div>
      )}

      {plan && (
        <div className="space-y-4">
          {plan.ideas.map((idea, i) => (
            <IdeaCard key={idea.id ?? `${week}-${i}`} idea={idea} slot={ideaSlot} />
          ))}
        </div>
      )}
    </section>
  );
}

function IdeaCard({
  idea,
  slot,
}: {
  idea: ContentIdea;
  slot?: (idea: ContentIdea) => ReactNode;
}) {
  return (
    <article className="gcm-card">
      <header className="mb-3 flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide-ui bg-gcm-brown text-text-inverted font-heading">
          {DAY_LABEL[idea.day] ?? idea.day}
        </span>
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide-ui bg-gcm-gold text-gcm-brown font-heading">
          {idea.pillar}
        </span>
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide-ui bg-gcm-red text-white font-heading">
          {PLATFORM_LABEL[idea.platform] ?? idea.platform}
        </span>
      </header>

      <h4 className="mb-2 text-text-primary">{idea.hook}</h4>
      <p className="text-text-primary font-body leading-relaxed">{idea.angle}</p>

      {idea.visual && (
        <div className="mt-3 rounded-md border border-gcm-gold/40 bg-gcm-gold/10 p-3">
          <span className="gcm-eyebrow text-gcm-gold">Visual idea</span>
          <p className="mt-1 text-sm text-text-primary font-body leading-relaxed">{idea.visual}</p>
        </div>
      )}

      {slot?.(idea)}
    </article>
  );
}
